import React, { useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { Button, InputGroupText, Tooltip } from 'src/components/ui';
import get from 'lodash/get';
import chunk from 'lodash/chunk';
import uniqBy from 'lodash/uniqBy';
import { UWEEntity } from '../api/types';
import useClickOutside from '../hooks/useClickOutside';
import { FormContextWith, FormDef } from './FormComponentTypes';
import { JSONSchemaWith } from '../util/jsonschemaTypes';
import getPathFromId from '../util/getPathFromId';
import Loader from './Loader';
import { useTheme } from '@emotion/react';
import { useDarkMode } from '../context/DarkModeContext';
import Icon from 'src/components/Icon';
import { toNumber } from 'lodash';

type Props = {
  currentEntity: UWEEntity | null;
  fieldId?: string | null;
  pastReports: UWEEntity[];
  loadingPastReports: boolean;
  errorLoadingPastReports: Error | undefined;
  formContext: FormContextWith<{
    subformDataPrefix?: string;
    subformDefinition?: FormDef;
  }>;
  formData: any;
  fieldSchema: JSONSchemaWith<{
    'akc:fieldDescription'?: string;
    'ui:sufix'?: string;
  }>;
};

type PastValue = {
  reportId: string;
  value: any;
  /** ISO date string e.g. "2026-03-27" from metadata.dueDate */
  dueDate: string | null;
  createdAt: string | null;
};

// Dark palette constants matching DarkModeContext
const DK = {
  text: '#e2e4e9',
  muted: '#878b9a',
  border: '#40444f',
  raised: '#32363d',
  surface: '#2a2d33',
  input: '#1c1e23',
} as const;

const buildPastValuePath = (
  path: string,
  formContext: FormContextWith<{
    subformDataPrefix?: string;
    subformDefinition?: FormDef;
  }>
): string => {
  const { subformDataPrefix = '', subformDefinition = null, formDefinition } = formContext;

  if (subformDataPrefix && subformDefinition != null) {
    const actualPath = path.replace(`${subformDataPrefix}.`, '');
    const pathToFieldInSubform = subformDefinition.invObjectMapAsMap[actualPath];
    const pathToSubformEntity = formDefinition.invObjectMapAsMap[subformDataPrefix];
    return `${pathToSubformEntity}.${pathToFieldInSubform}`;
  }

  return formDefinition.invObjectMapAsMap[path];
};

const getPastValuesForField = (
  path: string,
  pastReports: UWEEntity[] = [],
  formContext: FormContextWith<{
    subformDataPrefix?: string;
    subformDefinition?: FormDef;
  }>
): PastValue[] => {
  const pastValues: PastValue[] = [];
  const dataPath = buildPastValuePath(path, formContext);

  pastReports.forEach(rpt => {
    pastValues.push({
      reportId: rpt?.metadata?.reportingWindowPeriod ?? '(Report)',
      value: get(rpt, dataPath) ?? '--',
      dueDate: rpt?.metadata?.dueDate ?? null,
      createdAt: rpt?.createdAt,
    });
  });

  return pastValues;
};

/**
 * Computes the percentage change from a left node to the right node on the timeline.
 * Formula: ((right - left) / |left|) * 100
 * e.g. left=10, right=15 → +50%
 */
const computePercentageDiff = (leftValue: any, rightValue: any): number | null => {
  const left = parseFloat(leftValue);
  const right = parseFloat(rightValue);
  if (isNaN(left) || isNaN(right) || left === 0) return null;
  return ((right - left) / Math.abs(left)) * 100;
};

const DOT_SIZE = { current: 10, past: 8 };
const DOT_GAP = 5;
const LABEL_ROW_HEIGHT = 20;
const VALUE_ROW_HEIGHT = 28;

const TimelineNode = ({
  reportId,
  value,
  isCurrent,
  accentColor,
  diff,
  isNumberField,
}: {
  reportId: string;
  value: any;
  isCurrent: boolean;
  accentColor: string;
  diff: number | null;
  isNumberField: boolean;
}) => {
  const theme = useTheme();
  const { isDarkMode } = useDarkMode();
  const dotSize = isCurrent ? DOT_SIZE.current : DOT_SIZE.past;
  const diffColor = diff === 0 ? '#6b7280' : (diff || 0) > 0 ? theme.successMain : theme.dangerMain;
  const diffBg = diff === 0 ? (isDarkMode ? DK.raised : '#f3f4f6') : (diff || 0) > 0 ? `${theme.successMain}33` : `${theme.dangerMain}33`;
  const diffIcon = diff === null ? null : diff === 0 ? '–' : diff > 0 ? '▲' : '▼';
  const label = diff === null ? null : diff === 0 ? '0%' : `${diff > 0 ? '+' : ''}${diff.toFixed(1)}%`;

  let valueAsString = value;
  if (isNumberField && typeof value == 'number') {
    valueAsString = value.toLocaleString('en-US');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 60 }}>
      <div style={{ height: LABEL_ROW_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: DOT_GAP }}>
        <div
          style={{
            width: dotSize,
            height: dotSize,
            borderRadius: '50%',
            backgroundColor: accentColor,
            boxShadow: isCurrent ? `0 0 0 3px ${accentColor}30` : undefined,
            flexShrink: 0,
          }}
        />
        <div
          style={{
            fontSize: '0.62rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: accentColor,
          }}
        >
          {reportId}
        </div>
      </div>

      <div
        style={{
          height: VALUE_ROW_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          fontSize: isCurrent ? '1rem' : '0.82rem',
          fontWeight: isCurrent ? 700 : 500,
          color: isCurrent ? (isDarkMode ? DK.text : '#111827') : isDarkMode ? DK.muted : '#4b5563',
          lineHeight: 1,
        }}
      >
        <span>{valueAsString}</span>
        {diff !== null && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              fontSize: '0.62rem',
              fontWeight: 700,
              color: diffColor,
              background: diffBg,
              border: `1px solid ${diffColor}40`,
              borderRadius: 4,
              padding: '1px 4px',
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: '0.5rem' }}>{diffIcon}</span>
            {label}
          </span>
        )}
      </div>
    </div>
  );
};

const Legend = ({ currentColor, pastColor }: { currentColor: string; pastColor: string }) => {
  const theme = useTheme();
  const { isDarkMode } = useDarkMode();

  const items = [
    { color: currentColor, label: 'Current' },
    { color: pastColor, label: 'Past' },
  ];

  return (
    <div
      style={{
        display: 'flex',
        gap: 14,
        justifyContent: 'center',
        marginTop: 10,
        paddingTop: 8,
        borderTop: `1px solid ${isDarkMode ? DK.border : theme.gray200}`,
      }}
    >
      {items.map(({ color, label }) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.68rem', color: isDarkMode ? DK.text : theme.gray800 }}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: color }} />
          {label}
        </div>
      ))}
    </div>
  );
};

type TotalDiffProps = {
  firstNode: { reportId: string; value: any };
  lastNode: { reportId: string; value: any };
  diff: number;
};

const TotalDiff = ({ firstNode, lastNode, diff }: TotalDiffProps) => {
  const theme = useTheme();
  const { isDarkMode } = useDarkMode();
  const diffColor = diff === 0 ? '#6b7280' : diff > 0 ? theme.successMain : theme.dangerMain;
  const diffBg = diff === 0 ? (isDarkMode ? DK.raised : '#f3f4f6') : diff > 0 ? `${theme.successMain}33` : `${theme.dangerMain}33`;
  const diffIcon = diff === 0 ? '–' : diff > 0 ? '▲' : '▼';
  const label = diff === 0 ? '0%' : `${diff > 0 ? '+' : ''}${diff.toFixed(1)}%`;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 10,
        paddingTop: 8,
        borderTop: `1px solid ${isDarkMode ? DK.border : theme.gray200}`,
        gap: 8,
      }}
    >
      <div style={{ fontSize: '0.68rem', color: isDarkMode ? DK.text : theme.gray800, whiteSpace: 'wrap' }}>
        <Trans>Total difference</Trans>{' '}
        <div>
          <i style={{ color: isDarkMode ? DK.muted : theme.gray500 }}>
            ({lastNode.reportId} → {firstNode.reportId})
          </i>
        </div>
      </div>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          fontSize: '0.72rem',
          fontWeight: 700,
          color: diffColor,
          background: diffBg,
          border: `1px solid ${diffColor}40`,
          borderRadius: 4,
          padding: '2px 6px',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: '0.55rem' }}>{diffIcon}</span>
        {label}
      </div>
    </div>
  );
};

const FieldComparisonBtn = ({
  currentEntity,
  fieldId,
  pastReports,
  loadingPastReports,
  errorLoadingPastReports,
  formContext,
  formData,
  fieldSchema,
}: Props) => {
  const theme = useTheme();
  const { isDarkMode } = useDarkMode();
  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false);
  const dataPath = getPathFromId(fieldId);

  const pastValues = useMemo(() => {
    if (loadingPastReports || errorLoadingPastReports != null) return [];
    if (pastReports == null || !Array.isArray(pastReports)) return [];
    return getPastValuesForField(dataPath, pastReports, formContext);
  }, [dataPath, pastReports, loadingPastReports, errorLoadingPastReports, formContext]);

  const ref = useClickOutside(() => setIsTooltipOpen(false));
  const toggleTooltip = () => setIsTooltipOpen(prev => !prev);

  if (!fieldId) return null;
  const componentId = `field-comparison-${fieldId}`;
  const isNumberField = fieldSchema.type === 'number';

  const currentValue = {
    reportId: currentEntity?.metadata?.reportingWindowPeriod ?? 'Current',
    value: formData ? (isNumberField ? toNumber(`${formData}`.replace(/,/g, '')) : formData) : '--',
  };

  const renderTooltipContent = () => {
    if (loadingPastReports) return <Loader dots />;
    if (errorLoadingPastReports) return 'Error loading past reports…';

    const { 'akc:fieldDescription': fieldDesc, 'ui:sufix': suffix, title } = fieldSchema;

    const mostRecentFirst = [...pastValues].sort((a, b) => {
      if (a.dueDate == null && b.dueDate == null) return 0;
      if (a.dueDate == null) return 1;
      if (b.dueDate == null) return -1;
      if (a.dueDate === b.dueDate) {
        if (a.createdAt == null && b.createdAt == null) return 0;
        if (a.createdAt == null) return 1;
        if (b.createdAt == null) return -1;
        return b.createdAt.localeCompare(a.createdAt);
      }
      return b.dueDate.localeCompare(a.dueDate);
    });

    const allNodes = chunk(
      uniqBy(
        [
          { reportId: currentValue.reportId, value: currentValue.value, isCurrent: true },
          ...mostRecentFirst.map(pv => ({ reportId: pv.reportId, value: pv.value, isCurrent: false })),
        ],
        pv => pv.reportId
      ),
      3
    )[0].reverse();

    const totalDiff = isNumberField && allNodes.length >= 2 ? computePercentageDiff(allNodes[0].value, allNodes[allNodes.length - 1].value) : null;

    return (
      <div
        style={{
          position: 'relative',
          textAlign: 'left',
          fontWeight: 'normal',
          minWidth: 220,
          padding: '4px 2px 2px',
        }}
      >
        <Button
          size="sm"
          className="btn-sm btn-light text-danger p-0 px-1"
          style={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setIsTooltipOpen(false);
          }}
        >
          <Icon icon="X" weight="regular" />
        </Button>

        <div
          style={{
            marginRight: 28,
            paddingBottom: 10,
            fontWeight: 600,
            fontSize: '0.8rem',
            color: isDarkMode ? DK.text : theme.gray800,
            lineHeight: 1.3,
          }}
        >
          {fieldDesc ?? title}
          {suffix != null && <div style={{ fontWeight: 400, color: isDarkMode ? DK.muted : theme.gray500, marginLeft: 4 }}>({suffix})</div>}
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, paddingTop: 4, justifyContent: 'space-between' }}>
          {allNodes.map((node, i) => {
            const prevNode = allNodes[i - 1];
            const diff = isNumberField && prevNode != null ? computePercentageDiff(prevNode.value, node.value) : null;
            return (
              <React.Fragment key={node.reportId}>
                <TimelineNode
                  reportId={node.reportId}
                  value={node.value}
                  isCurrent={node.isCurrent}
                  accentColor={node.isCurrent ? theme.primaryMain : theme.infoMain}
                  isNumberField={isNumberField}
                  diff={diff}
                />
              </React.Fragment>
            );
          })}
        </div>

        {totalDiff !== null && <TotalDiff firstNode={allNodes[allNodes.length - 1]} lastNode={allNodes[0]} diff={totalDiff} />}

        <Legend currentColor={theme.primaryMain} pastColor={theme.infoMain} />
      </div>
    );
  };

  return (
    <React.Fragment key={`${componentId}-container`}>
      <InputGroupText
        id={componentId}
        className="input-group-append"
        style={{ cursor: 'pointer', background: isDarkMode ? DK.input : 'white' }}
        onClick={toggleTooltip}
      >
        <Icon icon="GitDiff" />
        <Tooltip
          className="field-comparison-tooltip"
          target={componentId}
          isOpen={isTooltipOpen}
          innerRef={ref}
          innerClassName="white-tooltip-inner"
          arrowClassName="white-tooltip-arrow"
          fade={false}
        >
          {renderTooltipContent()}
        </Tooltip>
      </InputGroupText>
    </React.Fragment>
  );
};

export default FieldComparisonBtn;
